import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Text,
  Divider,
} from '@fluentui/react-components';
import {
  DeleteRegular,
  EditRegular,
  CheckmarkRegular,
  DismissRegular,
  SearchRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import styles from './UrlAdmin.module.scss';
import { IUrlAdminProps } from './IUrlAdminProps';
import { getAuthToken } from '../../auth/getAuthToken';
import strings from './strings';

interface ShortUrlRecord {
  id: number;
  shortCode: string;
  longUrl: string;
  createdAt: string;
  createdByUpn: string;
}

interface PagedResponse {
  items: ShortUrlRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PAGE_SIZE = 25;

const UrlAdmin: React.FC<IUrlAdminProps> = () => {
  const { accounts } = useMsal();
  const [urls, setUrls] = useState<ShortUrlRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [newCode, setNewCode] = useState('');
  const [newLongUrl, setNewLongUrl] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Debounce search input; reset to page 1 on new query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Main fetch effect — re-runs on page, query, accounts, or explicit refresh
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      setLoading(true);
      const account = accounts[0];
      if (!account) { setLoading(false); return; }
      const token = await getAuthToken(account);
      if (!token || cancelled) {
        if (!cancelled) setError(strings.errorLoad);
        setLoading(false);
        return;
      }
      try {
        const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
        if (debouncedQuery) params.set('q', debouncedQuery);
        const res = await fetch(`/api/manage/urls?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) { setError(strings.errorLoad); return; }
        const data: PagedResponse = await res.json();
        setUrls(data.items);
        setTotalCount(data.totalCount);
        setTotalPages(data.totalPages);
        setPage(data.page);
      } catch {
        if (!cancelled) setError(strings.errorLoad);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, debouncedQuery, accounts, refreshKey]);

  const handleCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const shortCode = newCode.trim();
    const longUrl = newLongUrl.trim();
    if (!shortCode || !longUrl) return;
    setError(null);
    setCreating(true);
    const account = accounts[0];
    if (!account) return;
    const token = await getAuthToken(account);
    if (!token) { setError(strings.errorCreate); setCreating(false); return; }
    try {
      const res = await fetch('/api/manage/urls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ shortCode, longUrl }),
      });
      if (res.status === 409) { setError(strings.errorSlugTaken); return; }
      if (res.status === 400) { setError(strings.errorInvalidUrl); return; }
      if (!res.ok) { setError(strings.errorCreate); return; }
      setNewCode('');
      setNewLongUrl('');
      // New record sorts first; go to page 1 and force refresh
      setPage(1);
      setRefreshKey(k => k + 1);
    } catch {
      setError(strings.errorCreate);
    } finally {
      setCreating(false);
    }
  }, [newCode, newLongUrl, accounts]);

  const startEdit = (record: ShortUrlRecord) => {
    setEditingId(record.id);
    setEditValue(record.longUrl);
    setConfirmDeleteId(null);
  };

  const cancelEdit = () => { setEditingId(null); setEditValue(''); };

  const handleSave = useCallback(async (id: number, shortCode: string) => {
    const longUrl = editValue.trim();
    if (!longUrl) return;
    setError(null);
    setSavingId(id);
    const account = accounts[0];
    if (!account) return;
    const token = await getAuthToken(account);
    if (!token) { setError(strings.errorUpdate); setSavingId(null); return; }
    try {
      const res = await fetch(`/api/manage/urls/${shortCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ longUrl }),
      });
      if (res.status === 400) { setError(strings.errorInvalidUrl); return; }
      if (!res.ok) { setError(strings.errorUpdate); return; }
      // Optimistic local update — no page reload needed
      setUrls(prev => prev.map(u => u.id === id ? { ...u, longUrl } : u));
      setEditingId(null);
    } catch {
      setError(strings.errorUpdate);
    } finally {
      setSavingId(null);
    }
  }, [editValue, accounts]);

  const handleDelete = useCallback(async (id: number, shortCode: string) => {
    setError(null);
    setDeletingId(id);
    const account = accounts[0];
    if (!account) return;
    const token = await getAuthToken(account);
    if (!token) { setError(strings.errorDelete); setDeletingId(null); return; }
    try {
      const res = await fetch(`/api/manage/urls/${shortCode}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setError(strings.errorDelete); return; }
      // Step back a page if this was the last item on the current page
      const remainingOnPage = urls.filter(u => u.id !== id).length;
      setConfirmDeleteId(null);
      setPage(remainingOnPage === 0 && page > 1 ? page - 1 : page);
      setRefreshKey(k => k + 1);
    } catch {
      setError(strings.errorDelete);
    } finally {
      setDeletingId(null);
    }
  }, [accounts, urls, page]);

  return (
    <section className={styles.container} aria-label={strings.heading}>
      <Text as="h2" weight="semibold" size={500} className={styles.heading}>
        {strings.heading}
      </Text>

      <form className={styles.createForm} onSubmit={handleCreate}>
        <Input
          value={newCode}
          onChange={(_, d) => setNewCode(d.value)}
          placeholder={strings.createCodePlaceholder}
          aria-label={strings.createCodeLabel}
          autoComplete="off"
          appearance="outline"
          className={styles.createInput}
        />
        <Input
          value={newLongUrl}
          onChange={(_, d) => setNewLongUrl(d.value)}
          placeholder={strings.createUrlPlaceholder}
          aria-label={strings.createUrlLabel}
          autoComplete="off"
          appearance="outline"
          className={styles.createUrlInput}
        />
        <Button
          appearance="primary"
          type="submit"
          disabled={creating || !newCode.trim() || !newLongUrl.trim()}
        >
          {strings.createButton}
        </Button>
      </form>

      {error && (
        <MessageBar intent="error" aria-live="assertive">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.searchBar}>
        <Input
          value={searchQuery}
          onChange={(_, d) => setSearchQuery(d.value)}
          placeholder={strings.searchPlaceholder}
          aria-label={strings.searchLabel}
          autoComplete="off"
          appearance="outline"
          className={styles.searchInput}
          contentBefore={<SearchRegular />}
        />
        {searchQuery && (
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            onClick={() => setSearchQuery('')}
            aria-label={strings.searchClearLabel}
          />
        )}
      </div>

      <Divider />

      {loading ? (
        <Text className={styles.emptyState}>{strings.loading}</Text>
      ) : urls.length === 0 ? (
        <Text className={styles.emptyState}>
          {debouncedQuery ? strings.emptyStateSearch : strings.emptyState}
        </Text>
      ) : (
        <>
          <div className={styles.table} role="list">
            {urls.map(u => (
              <div key={u.id} className={styles.row} role="listitem">
                <Text className={styles.code}>{u.shortCode}</Text>

                {editingId === u.id ? (
                  <Input
                    value={editValue}
                    onChange={(_, d) => setEditValue(d.value)}
                    className={styles.editInput}
                    aria-label={strings.editUrlLabel}
                    autoFocus
                  />
                ) : (
                  <Text className={styles.url} title={u.longUrl}>{u.longUrl}</Text>
                )}

                <div className={styles.actions}>
                  {editingId === u.id ? (
                    <>
                      <Button
                        appearance="primary"
                        size="small"
                        icon={<CheckmarkRegular />}
                        disabled={savingId === u.id || !editValue.trim()}
                        onClick={() => handleSave(u.id, u.shortCode)}
                        aria-label={strings.saveButton}
                      >
                        {strings.saveButton}
                      </Button>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        onClick={cancelEdit}
                        aria-label={strings.cancelButton}
                      >
                        {strings.cancelButton}
                      </Button>
                    </>
                  ) : confirmDeleteId === u.id ? (
                    <>
                      <Button
                        appearance="primary"
                        size="small"
                        disabled={deletingId === u.id}
                        onClick={() => handleDelete(u.id, u.shortCode)}
                        aria-label={strings.confirmDeleteButton}
                        className={styles.confirmButton}
                      >
                        {strings.confirmDeleteButton}
                      </Button>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        onClick={() => setConfirmDeleteId(null)}
                        aria-label={strings.cancelButton}
                      >
                        {strings.cancelButton}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<EditRegular />}
                        onClick={() => startEdit(u)}
                        aria-label={`${strings.editButton} ${u.shortCode}`}
                      >
                        {strings.editButton}
                      </Button>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DeleteRegular />}
                        onClick={() => { setConfirmDeleteId(u.id); setEditingId(null); }}
                        aria-label={`${strings.deleteButton} ${u.shortCode}`}
                        className={styles.deleteButton}
                      >
                        {strings.deleteButton}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.pagination} aria-label={strings.paginationLabel}>
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronLeftRegular />}
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => p - 1)}
              aria-label={strings.prevPage}
            />
            <Text size={200}>{strings.pageInfo(page, totalPages, totalCount)}</Text>
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronRightRegular />}
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => p + 1)}
              aria-label={strings.nextPage}
            />
          </div>
        </>
      )}
    </section>
  );
};

export default UrlAdmin;
